import {
    useBookableResourcesQuery,
    useEventTypesQuery,
} from '../../../../lib/SharedQueries.js';
import { useEffect } from 'react';
import { getBookWithLabel } from '../../../../lib/BookableResourceHelpers.js';
import SelectionBlock from '../components/SelectionBlock.jsx';
import ErrorMessage from '../../../ErrorMessage';
import { useQuestionEventTypes } from '~/components/forms/questions/scheduling/SelectEventType.jsx';
import { numberBookableResourcesRequiredFromId } from '~/lib/Questions/SchedulingHelpers.js';

export function getAnyAvailableCalendarId(settings) {
    let id = null;
    if (settings?.poolingPrioritization === 'prioritized') {
        id = -2;
    } else {
        id = -1;
    }

    if (settings?.bookableResources?.length > 0) {
        // Add filter
        const filter = settings?.bookableResources.join(',');
        return `${id}:${filter}`;
    } else {
        return id;
    }
}

export function useQuestionBookableResources(eventTypeId, startTime, settings) {
    const { isLoading, data } = useBookableResourcesQuery(
        eventTypeId,
        startTime,
        settings?.eventTypes,
    );

    let bookableResources = data || [];
    if (!bookableResources.filter) {
        return [];
    }

    bookableResources = bookableResources.filter(type => {
        if (
            settings?.bookableResources &&
            settings?.bookableResources.length > 0
        ) {
            return settings.bookableResources.indexOf(type.id) > -1;
        } else {
            return true;
        }
    });

    return {
        isLoading,
        bookableResources,
    };
}

const SelectBookableResource = ({
    value,
    startTime,
    selectedRightContent,
    onChange,
    onChangeCapacity,
    settings,
    eventTypeId,
}) => {
    const { isLoading, bookableResources } = useQuestionBookableResources(
        eventTypeId,
        startTime,
        settings,
    );

    const { types } = useQuestionEventTypes(null, settings, true);
    const numBookableResourcesRequired = numberBookableResourcesRequiredFromId(
        eventTypeId,
        types,
    );

    let resourceOptions = bookableResources.map(resource => ({
        title: resource.name,
        description: resource.description || '',
        value: resource.id,
    }));

    if (
        !startTime &&
        !!eventTypeId &&
        settings?.pooledAvailability === 'optional' &&
        resourceOptions?.length > numBookableResourcesRequired
    ) {
        resourceOptions = [
            {
                title: 'Any Available',
                description: '',
                value: getAnyAvailableCalendarId(settings),
                excludeFromMultiSelect: true,
            },
            ...resourceOptions,
        ];
    }

    if (
        !startTime &&
        !!eventTypeId &&
        settings?.pooledAvailability === 'required'
    ) {
        resourceOptions = [
            {
                title: 'Any Available',
                description: '',
                value: getAnyAvailableCalendarId(settings),
                excludeFromMultiSelect: true,
            },
        ];
    }

    function update(newValue) {
        const normalizedNewValue = Array.isArray(newValue)
            ? newValue.join(',')
            : newValue?.toString();
        const normalizedOldValue = Array.isArray(value)
            ? value.join(',')
            : value?.toString();

        if (
            //newValue === value ||
            normalizedNewValue === normalizedOldValue
        ) {
            // Don't update, and possibly reset things, if we're currently set
            return;
        }

        onChange(newValue);

        const ids = Array.isArray(newValue) ? newValue : [newValue];
        const resourceData = bookableResources.filter(br =>
            ids.includes(br.id),
        );
        if (resourceData?.length) {
            const minCapacity = Math.min(...resourceData.map(r => r.capacity));
            onChangeCapacity(minCapacity);
        }
    }

    useEffect(() => {
        if (resourceOptions.length === numBookableResourcesRequired) {
            if (numBookableResourcesRequired === 1) {
                update(resourceOptions[0].value);
            } else {
                update(resourceOptions.map(o => o.value));
            }
        }
    }, [JSON.stringify(resourceOptions), numBookableResourcesRequired]);

    // We're explicitly hiding
    //  or there's only 1 and we're not coming from a start time
    if (
        settings?.hideBookableResource ||
        (!startTime &&
            resourceOptions.length === 1 &&
            resourceOptions[0].value === value)
    ) {
        return null;
    }

    const bookWithLabel = getBookWithLabel(bookableResources);

    return (
        <div>
            {isLoading && <div>Loading...</div>}
            <div className="mt-4 input-label">{bookWithLabel}</div>
            <SelectionBlock
                value={value}
                onChange={update}
                options={resourceOptions}
                selectedRightContent={selectedRightContent}
                numberSelectionsRequired={numBookableResourcesRequired}
            />
            {resourceOptions.length === 0 && !isLoading && (
                <ErrorMessage>
                    There is nothing that can be booked.
                </ErrorMessage>
            )}
            {!!settings?.isError && numBookableResourcesRequired > 1 && (
                <ErrorMessage>
                    You must select {numBookableResourcesRequired} options
                </ErrorMessage>
            )}
        </div>
    );
};

export default SelectBookableResource;
